import { Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { ApiError } from '../../core/models/api-error.model';
import { Make, MODEL_YEAR_RANGE, VehicleModel, VehicleType } from '../../core/models/vehicle.model';
import { VehicleCatalogService } from '../../core/services/vehicle-catalog.service';
import { MakeSelect } from './components/make-select/make-select';
import { ModelsList } from './components/models-list/models-list';
import { VehicleTypeSelect } from './components/vehicle-type-select/vehicle-type-select';
import { YearInput } from './components/year-input/year-input';

@Component({
  selector: 'app-vehicle-explorer',
  imports: [MakeSelect, YearInput, VehicleTypeSelect, ModelsList],
  templateUrl: './vehicle-explorer.html',
  styleUrl: './vehicle-explorer.scss',
})
export class VehicleExplorer {
  private readonly catalog = inject(VehicleCatalogService);

  readonly makes = signal<Make[]>([]);
  readonly makesLoading = signal(false);
  readonly makesError = signal<string | null>(null);

  readonly selectedMakeId = signal<number | null>(null);

  readonly yearControl = new FormControl<number | null>(
    { value: null, disabled: true },
    { validators: [Validators.min(MODEL_YEAR_RANGE.min), Validators.max(MODEL_YEAR_RANGE.max)] },
  );
  private readonly yearRaw = toSignal(this.yearControl.valueChanges, { initialValue: this.yearControl.value });
  readonly yearValid = computed(() => this.yearRaw() != null && this.yearControl.valid);

  readonly selectedVehicleType = signal<string | null>(null);
  readonly vehicleTypesLoading = signal(false);
  readonly vehicleTypesError = signal<string | null>(null);

  readonly modelsLoading = signal(false);
  readonly modelsError = signal<string | null>(null);
  readonly modelsReady = computed(() => this.selectedVehicleType() !== null);

  private readonly vehicleTypesQuery = computed(() => ({
    makeId: this.selectedMakeId(),
    year: this.yearValid() ? this.yearRaw() : null,
  }));

  readonly vehicleTypes = toSignal(
    toObservable(this.vehicleTypesQuery).pipe(
      tap(() => {
        this.selectedVehicleType.set(null);
        this.vehicleTypesError.set(null);
      }),
      switchMap(({ makeId, year }) => {
        if (makeId == null || year == null) {
          return of<VehicleType[]>([]);
        }
        this.vehicleTypesLoading.set(true);
        return this.catalog.getVehicleTypes(makeId).pipe(
          catchError((error: ApiError) => {
            this.vehicleTypesError.set(error.message);
            return of<VehicleType[]>([]);
          }),
          finalize(() => this.vehicleTypesLoading.set(false)),
        );
      }),
    ),
    { initialValue: [] as VehicleType[] },
  );

  private readonly modelsQuery = computed(() => ({
    makeId: this.selectedMakeId(),
    year: this.yearValid() ? this.yearRaw() : null,
    vehicleType: this.selectedVehicleType(),
  }));

  readonly models = toSignal(
    toObservable(this.modelsQuery).pipe(
      tap(() => this.modelsError.set(null)),
      switchMap(({ makeId, year, vehicleType }) => {
        if (makeId == null || year == null || vehicleType == null) {
          return of<VehicleModel[]>([]);
        }
        this.modelsLoading.set(true);
        return this.catalog.getModels(makeId, year, vehicleType).pipe(
          catchError((error: ApiError) => {
            this.modelsError.set(error.message);
            return of<VehicleModel[]>([]);
          }),
          finalize(() => this.modelsLoading.set(false)),
        );
      }),
    ),
    { initialValue: [] as VehicleModel[] },
  );

  constructor() {
    this.loadMakes();
  }

  onMakeChanged(makeId: number | null): void {
    if (this.selectedMakeId() === makeId) {
      return;
    }

    this.selectedMakeId.set(makeId);

    if (makeId === null) {
      this.yearControl.reset();
      this.yearControl.disable();
    } else {
      this.yearControl.enable();
    }
  }

  onVehicleTypeChanged(vehicleType: string | null): void {
    this.selectedVehicleType.set(vehicleType);
  }

  private loadMakes(): void {
    this.makesLoading.set(true);
    this.catalog
      .getMakes()
      .pipe(
        catchError((error: ApiError) => {
          this.makesError.set(error.message);
          return of<Make[]>([]);
        }),
        finalize(() => this.makesLoading.set(false)),
      )
      .subscribe((makes) => this.makes.set(makes));
  }
}
